import {
    Color,
    Mesh,
    MeshPhysicalMaterial,
    MeshStandardMaterial,
    Object3D,
    Texture,
    Vector2,
    Vector3,
    Vector4,
} from "three";
import { ScenesManager } from "../ScenesManager";

const MAX_FADE_POINTS = 13;


const fadeUniforms = {
    fadePoints: {
        value: new Array(MAX_FADE_POINTS).fill(new Vector4(9999, 9999, 9999, 1)),
    },
    fadeSmoothness: { value: new Array(MAX_FADE_POINTS).fill(0.2) },
    fadeStretch: { value: new Array(MAX_FADE_POINTS).fill(new Vector2(1, 1)) },
    fadeCount: { value: 0 },
    bypass: { value: 0 },
};

export const setFadePoints = (
    bypass: Boolean,
    obj: { point: Vector4; smoothness: Number; stretch: Vector2 }[]
) => {
    fadeUniforms.fadeCount.value = obj.length;
    obj.forEach((item, i) => {
        fadeUniforms.fadePoints.value[i] = item.point.clone();
        fadeUniforms.fadeSmoothness.value[i] = item.smoothness;
        fadeUniforms.fadeStretch.value[i] = item.stretch;
    });

    if (bypass) {
        fadeUniforms.bypass.value = 1;
    }
};

export const prepareTransition = (
    material: MeshStandardMaterial | MeshPhysicalMaterial,
    uniforms: { progress: { value: number }; uSampler: { value: Texture } }
) => {
    if (material && material.onBeforeCompile) {
        
        material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, fadeUniforms);

            shader.uniforms.progress = uniforms.progress;
            shader.uniforms.uSampler = uniforms.uSampler;

            shader.vertexShader = shader.vertexShader.replace(
                "varying vec3 vViewPosition;",
            /*glsl*/`
                varying vec3 vViewPosition;
                varying float posY;
                varying vec2 vUv;
                varying vec3 vWorldPosition2;
            `
            );

            shader.vertexShader = shader.vertexShader.replace(
                "#include <fog_vertex>",
            /*glsl*/`
                #include <fog_vertex>
                vUv = uv;
                posY = (modelMatrix * vec4( transformed, 1.0 )).y;
            `
            );

            shader.vertexShader = shader.vertexShader.replace(
                "#include <worldpos_vertex>",
            /*glsl*/`
                #include <worldpos_vertex>
                vWorldPosition2 = worldPosition.xyz;
            `
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <common>",
            /*glsl*/`
                #include <common>
                varying vec3 vWorldPosition2;
                uniform vec4 fadePoints[${MAX_FADE_POINTS}]; // vec3 = pos, w = radius
                uniform float fadeSmoothness[${MAX_FADE_POINTS}];
                uniform vec2 fadeStretch[${MAX_FADE_POINTS}];
                uniform int fadeCount;
                uniform int bypass;

                float getRadialFade(vec3 pos) {
                    float result = 0.0;

                    if (bypass > 0) {
                    return 1.0;
                    }

                    for (int i = 0; i < ${MAX_FADE_POINTS}; i++) {
                    if (i >= fadeCount) break;

                        vec3 center = fadePoints[i].xyz;
                        float radius = fadePoints[i].w;
                        float smoothness = fadeSmoothness[i];
                        vec2 stretch = fadeStretch[i];

                        vec3 local = pos - center;
                        local.x /= stretch.x;
                        local.z /= stretch.y;

                        float d = length(local);
                        float f = smoothstep(radius * smoothness, radius, d);

                        result = max(result, 1.0 - f); // keep max for additive blending
                    }

                    return result;
                }
            `
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <envmap_physical_pars_fragment>",
            /*glsl*/`
                // vec3 getIBLIrradiance( const in vec3 normal ) {
                //     return vec3(0.0);
                // }

                vec3 getIBLIrradiance( const in vec3 normal ) {

                    #ifdef ENVMAP_TYPE_CUBE_UV
            
                        vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );
            
                        vec4 envMapColor = textureCubeUV( envMap, envMapRotation * worldNormal, 1.0 );
            
                        return PI * envMapColor.rgb * envMapIntensity;
            
                    #else
            
                        return vec3( 0.0 );
            
                    #endif
            
                }

            
                vec3 getIBLRadiance( const in vec3 viewDir, const in vec3 normal, const in float roughness ) {
                    vec3 reflectVec = reflect(-viewDir, normal);
                    reflectVec = normalize(mix(reflectVec, normal, roughness * roughness));
                    reflectVec = inverseTransformDirection(reflectVec, viewMatrix);
                    vec4 envMapColor = textureCubeUV(envMap, envMapRotation * reflectVec, roughness);
                
                    float fade = getRadialFade(vWorldPosition2);
                    float finalIntensity = envMapIntensity * fade;
                
                    return envMapColor.rgb * finalIntensity;
                }
            `
            );

            shader.fragmentShader = /*glsl*/`
                #define ss(a,b,c) smoothstep(a,b,c)
                uniform float progress;
                uniform sampler2D uSampler;
                varying float vPos;
                varying vec2 vUv;            
            
                ${shader.fragmentShader}
          `.replace(
            /*glsl*/`vec4 diffuseColor = vec4( diffuse, opacity );`,
            /*glsl*/`
                vec4 col = vec4(0.0);
                vec4 textureColor1 = texture2D(uSampler, vUv);
            
                float fade = getRadialFade(vWorldPosition2);
            
                vec3 finalColor = mix(diffuse, col.rgb, progress);
                finalColor *= fade;
            
                float finalAlpha = (opacity - progress * (1. - progress) - 0.1) * fade;
            
                vec4 diffuseColor = vec4(finalColor, finalAlpha);
            `
            );
        };
        // }
    }
};

export const applyMaterials = (
    model: Object3D,
    carpaint: MeshStandardMaterial,
    uniforms: { progress: { value: number }; uSampler: { value: Texture } },
    globalEnvMapIntensity: number,
    glassEnvMapIntensity: number,
    fullModel = false
) => {
    const params = {
        color: ScenesManager.EXTERNAL_REVEALEAD ? 0xa6a6a6 : 0x000000,
        transmission: 1,
        opacity: 1,
        metalness: 0,
        roughness: 0.05,
        ior: 1.5,
        thickness: 0.01,
        specularIntensity: 0.5,
        specularColor: 0xff0000,
        envMapIntensity: 1,
    };

    const glassMaterial = new MeshPhysicalMaterial({
        color: params.color,
        metalness: params.metalness,
        roughness: params.roughness,
        ior: params.ior,
        envMap: ScenesManager.assets.hdrCar,
        envMapIntensity: params.envMapIntensity,
        transmission: params.transmission,
        specularIntensity: params.specularIntensity,
        specularColor: params.specularColor,
        opacity: params.opacity,
        transparent: true,
    });
    // const logoMaterial = new MeshPhysicalMaterial({
    //     color: 0xffffff,
    //     metalness: 1,
    //     roughness: params.roughness,
    // });

    model.traverse((child) => {
        if (child) {
            child.renderOrder = 2;
            child.castShadow = true;
            let material = (child as Mesh).material as MeshStandardMaterial;

            if (child.name.indexOf("vidro_farolGrade") > -1) {
                const isFarolActive = ScenesManager.EXTERNAL_FEATURES_DATA['farolGrade'].active;
                material.transparent = isFarolActive;
                material.roughness = isFarolActive ? 0 : 1;
                child.renderOrder = 3;
                
            } else if (child.name.indexOf("vidro_farolTraseiro") > -1) {
                const isFarolActive = true;
                material.transparent = isFarolActive;
                material.roughness = isFarolActive ? 0 : 1;
                child.renderOrder = 3;
            }

            if (child.name.indexOf("base") > -1) {
                child.visible = false;
            }
            

            const isGlass = material && child.name === "glass" || child.name.indexOf("feature_tetoSolar") > -1;
            const isTeto = material && child.name.indexOf("feature_tetoSolar") > -1;

            if (isGlass) {
                (child as Mesh).material = glassMaterial.clone();

                if (isTeto) {                        
                    const mat = (child as Mesh).material as MeshPhysicalMaterial;
                    let feat = ScenesManager.EXTERNAL_FEATURES_DATA["tetoSolar"];
                    const color = ScenesManager.EXTERNAL_REVEALEAD ? 0xa6a6a6 : feat?.active ? 0x959595 : 0x000000;
                    mat.color = new Color(color);                    
                    mat.roughness = feat?.active ? 0.05 : 1;
                    mat.envMapIntensity = feat?.active ? 1 : 0;

                    
                }
                           
            } else {
                if (material) {
                    ((child as Mesh).material as MeshStandardMaterial).envMap =
                        ScenesManager.assets.hdrCar;
                    ((child as Mesh).material as MeshStandardMaterial).envMapIntensity =
                        globalEnvMapIntensity;
                }
            }

            let shouldApplyTransition = !fullModel;

            const uniformsClone = {
                progress: { value: uniforms.progress.value },
                uSampler: { value: uniforms.uSampler.value },
            };
            if (!isGlass) {
                let name = child.name.replace("feature_", "");
                let feat = ScenesManager.EXTERNAL_FEATURES_DATA[name];
                if (!feat && child.parent) {
                    name = child.parent.name.replace("feature_", "");
                    feat = ScenesManager.EXTERNAL_FEATURES_DATA[name];

                }
                if (feat) {
                    child.userData.isFeature = true;
                    child.userData.isFeatureActive = feat.active;
                } else {
                    child.userData.isFeature = false;
                }

                if (child && child.name.indexOf('farois_traseiros') > -1) {          
                    const color = ScenesManager.EXTERNAL_FEATURES_DATA['lanternaTraseira'].active ? new Color(0xFF0000) : new Color(0x000000);
                    (child as Mesh).material = new MeshStandardMaterial({
                      color: color, 
                      opacity: 1.0, 
                      emissive: color, 
                      emissiveIntensity: 10.0
                  });
                }

                
                if (feat && feat.active) {
                    if (!ScenesManager.featureActiveObjs[name]) {
                        ScenesManager.featureActiveObjs[name] = [];
                    }

                    const material = (child as Mesh).material as MeshStandardMaterial;
                    const materialClone = material ? material.clone() : material;
                    // if (name.indexOf("teto") < 0) {

                    //   if (materialClone && materialClone.envMapIntensity) {
                    //       materialClone.envMapIntensity = globalEnvMapIntensity;
                    //   }
                    //   (child as Mesh).material = materialClone;
                    // }

                    if (child.name === "feature_retrovisor") {
                        let retrovisor1 = model.getObjectByName("body_retrovisor");
                        let retrovisor2 = model.getObjectByName("body_retrovisor001");
                        let retrovisor3 = model.getObjectByName("body_retrovisor003");

                        if (retrovisor1) retrovisor1.userData.isFeatureActive = true;
                        if (retrovisor2) retrovisor2.userData.isFeatureActive = true;
                        if (retrovisor3) retrovisor3.userData.isFeatureActive = true;
                    } 

                    if (shouldApplyTransition) {
                        prepareTransition(materialClone, uniformsClone);
                        child.userData.material = materialClone;
                        child.userData.uniforms = uniformsClone;
                    } else {
                        // child.layers.enable( ScenesManager.BLOOM_SCENE )
                    }
                    if (!fullModel) {
                        ScenesManager.featureActiveObjs[name].push(child as Mesh);
                    }
                }
                if (shouldApplyTransition) {
                    const materialOriginal = (child as Mesh)
                        .material as MeshStandardMaterial;
                    let materialClone = materialOriginal;
                    if (materialOriginal) {
                        materialClone = materialOriginal.clone();
                    }
                    if (materialClone && materialClone.envMapIntensity) {
                        materialClone.envMapIntensity = globalEnvMapIntensity;
                    }
                    (child as Mesh).material = materialClone;
                    prepareTransition(materialClone, uniformsClone);
                    child.userData.material = materialClone;
                    child.userData.uniforms = uniformsClone;
                }
            }
            // if ((!shouldApplyTransition && ((child as Mesh).material))) {
            //     let intensity = 1;
            //     ((child as Mesh).material as MeshStandardMaterial).envMapIntensity = intensity;
            // }
        }
    });
};
